import { Divider, Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ICandidate, EmailLog, ICandidateEmailLogPayload, ICandidateEmailLog } from 'wisestep-api';
import {
  WsTypography,
  WsColors,
  ScrollableTabs,
  TabTextVariant,
  WsPaginationBar,
  getDateUsingFormat,
  WsListingLoader,
} from 'wisestep-base';

import { CandidateEmailHeader } from '../../../features/candidateLayout/components/communicationLogs';
import useCandidateEmailLogs from '../../../features/candidateLayout/hooks/useCandidateEmailLogs';
import useCandidateEmailLogThread from '../../../features/candidateLayout/hooks/useCandidateEmailLogThread';
import { EmailBody, EmailSubject } from '../../../features/emailSettings/components/emailContent';
import { EmailPreviewCard } from '../../../features/emailSettings/components/emailPreviewCard';
import {
  EmailBodyContainer,
  EmailContentWrapper,
  EmailLayoutGrid,
  Sidebar,
} from '../../../features/emailSettings/components/layout/emailLayout';
import { NoEmailCampaignResponse } from '../../../features/emailSettings/components/noEmailResponse';
import SearchBar from '../../../features/teams/components/SearchBarWrapper';

interface IEmailLogs {
  candidate: ICandidate;
  amplitudeEventHandler?: (eventName: string) => void;
}

const EmailLogs = ({ candidate }: IEmailLogs) => {
  const [search, setSearch] = useState<ICandidateEmailLogPayload>({
    candidateId: candidate?.candidateId ?? '',
    page: 0,
    size: 20,
  } as ICandidateEmailLogPayload);
  const [selectedItem, setSelectedItem] = useState<number>(0);

  const { candidateUniqueEmailLogs, loading } = useCandidateEmailLogs(search);
  const { loading: emailThreadLoading, candidateEmailLogThread } = useCandidateEmailLogThread(
    search?.candidateId ?? '',
    candidateUniqueEmailLogs?.content?.[selectedItem]?.campaignCode ?? ''
  );

  const methods = useForm();

  const index = 0;

  const handlePageChange = (newPage: number) => {
    setSearch({ ...search, page: newPage });
    setSelectedItem(0);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setSearch({ ...search, size: newPageSize, page: 0 });
    setSelectedItem(0);
  };
  return (
    <>
      {loading ? (
        <WsListingLoader />
      ) : !isEmpty(candidateUniqueEmailLogs.content) ? (
        <Grid px={2}>
          <WsTypography>Email Logs</WsTypography>
          <ScrollableTabs
            defaultValue={index}
            scrollList={[{ tabName: 'sent' }]}
            getTabIndex={() => {}}
            variant={TabTextVariant.SUB_TITLE}
          />
          <Grid px={2}>
            {index == 0 && (
              <Grid display="flex" direction="column" py={2}>
                <FormProvider {...methods}>
                  <SearchBar name="query" />
                </FormProvider>
                <WsPaginationBar
                  totalRecords={candidateUniqueEmailLogs.totalElements}
                  page={search.page}
                  pageSize={search.size}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
                <Divider />
                <EmailLayoutGrid>
                  <Sidebar xs={3} display="flex" direction="column">
                    {candidateUniqueEmailLogs?.content?.map((email, index) => (
                      <Grid
                        item
                        width="100%"
                        key={index}
                        onClick={() => setSelectedItem(index)}
                        sx={{
                          bgcolor: selectedItem === index ? WsColors.BackGroundBrandLight : '',
                        }}
                      >
                        <EmailPreviewCard
                          userName={email?.from ?? 'Not Available'}
                          subject={email?.subject ?? 'Not Available'}
                          emailSentTime={
                            email?.createdOn
                              ? getDateUsingFormat(email.createdOn.toString(), 'DD MMM')
                              : ''
                          }
                          bodyPreview={email?.body ?? 'Not Available'}
                          jobInfo={`${email?.jobTitle} (${email?.jobCode})`}
                        />
                        <Divider />
                      </Grid>
                    ))}
                  </Sidebar>

                  <EmailContentWrapper key={selectedItem} xs={9}>
                    <CandidateEmailHeader
                      isCampaignEmail={candidateEmailLogThread.length > 1}
                      candidateEmailLog={
                        candidateUniqueEmailLogs?.content?.[selectedItem] ??
                        ({} as ICandidateEmailLog)
                      }
                    />

                    <Grid px={2} display="flex" direction="column" rowGap={2}>
                      <EmailSubject
                        emailsubject={
                          candidateUniqueEmailLogs?.content?.[selectedItem]?.subject ??
                          'Not Available'
                        }
                        emailType={
                          candidateEmailLogThread.length > 1 ? 'Campaign Email' : 'Recruiter Email'
                        }
                      />
                      {emailThreadLoading ? (
                        <WsListingLoader />
                      ) : (
                        candidateEmailLogThread?.map((emailLog, index) => {
                          const isLastMessage = index === candidateEmailLogThread.length - 1;

                          return (
                            <EmailBodyContainer item key={index}>
                              <EmailBody
                                emailLog={(emailLog as EmailLog) ?? ({} as EmailLog)}
                                defaultExpand={isLastMessage}
                                showReply={false}
                                recruiterAccess={false}
                              />
                              {!isLastMessage && <Divider />}
                            </EmailBodyContainer>
                          );
                        })
                      )}
                    </Grid>
                  </EmailContentWrapper>
                </EmailLayoutGrid>
              </Grid>
            )}
          </Grid>
        </Grid>
      ) : (
        <NoEmailCampaignResponse text="No Email Logs" subText="Updated email logs appear here" />
      )}
    </>
  );
};

export default EmailLogs;
